import {css, StyleSheet} from 'aphrodite';
import React, {useState, useEffect, useMemo} from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import {GenomeOption} from 'constants/GenomeConstants';
import staticURL from 'lib/staticURL';
import GenomeStore from 'stores/GenomeStore';
import {FONT_PROXIMA_12} from 'styles/FontConstants';
import {GRAY_DARK} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {AsyncStatus, useAsyncStateOnMount} from 'ui-library/hooks/useAsyncState';
import {useDebounce} from 'ui-library/hooks/useDebounce';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import TextInput from 'ui-library/inputs/TextInput';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';
import SectionHeader from 'ui-library/menus/SectionHeader';
import escapeRegExp from 'util/escapeRegExp';
import {genomeToName} from 'util/GenomeUtil';
import {assertNever} from 'util/Types';

const ENSEMBL_SPECIES_LINK = staticURL('/static/molbio/ensemblSpeciesConstants.json');

// Max number of species results to show at once in a section, since rendering
// all 30k+ Ensembl species has poor performance and is not necessary
const MAX_ENSEMBL_OPTIONS = 50;

export enum GenomeSource {
  ENSEMBL = 'ENSEMBL',
  PUBLIC = 'PUBLIC',
  PRIVATE = 'PRIVATE',
}

const GENOME_SECTION_HEADER: {
  [key in GenomeSource]: string | null;
} = {
  [GenomeSource.PUBLIC]: null,
  [GenomeSource.PRIVATE]: 'Private Genomes',
  [GenomeSource.ENSEMBL]: 'Ensembl Genomes',
};

export interface GenomeSearchSelectorProps {
  disabled?: boolean;
  selectedValue?: GenomeOption;
  onSelectGenome: (value: GenomeOption) => void;
  genomeSources: Array<GenomeSource>;
}

function PresentationalGenomeSearchSelector({
  disabled = false,
  selectedValue,
  onSelectGenome,
  genomeSources,
  ensemblSpecies,
}: GenomeSearchSelectorProps & {ensemblSpecies: Array<GenomeOption>}): JSX.Element {
  const [searchQuery, setSearchQuery] = useState(selectedValue?.ensemblName ?? '');
  const allMenuOptions = useMemo(getAllMenuOptions, [genomeSources, ensemblSpecies]);
  const [menuOptions, setMenuOptions] = useState(allMenuOptions);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const searchQueryDebounced = useDebounce(searchQuery, 250);

  function getAllMenuOptions(): {
    [key in GenomeSource]: Array<GenomeOption>;
  } {
    const options: {
      [key in GenomeSource]: Array<GenomeOption>;
    } = {[GenomeSource.PUBLIC]: [], [GenomeSource.PRIVATE]: [], [GenomeSource.ENSEMBL]: []};

    if (genomeSources.includes(GenomeSource.ENSEMBL)) {
      options[GenomeSource.ENSEMBL] = ensemblSpecies;
    }
    const storedGenomes = GenomeStore.getGenomes().map((genome) => ({
      ...genome,
      displayName: genomeToName(genome),
    }));
    const orderedOptions = _.chain(storedGenomes)
      .sortBy((genomeOption) => genomeOption.ucscName)
      .reverse()
      .sortBy((genomeOption) => genomeOption.organismName)
      .value();
    orderedOptions.forEach((option) => {
      if (option.hidden && genomeSources.includes(GenomeSource.PRIVATE)) {
        options[GenomeSource.PRIVATE].push(option);
      } else if (!option.hidden && genomeSources.includes(GenomeSource.PUBLIC)) {
        options[GenomeSource.PUBLIC].push(option);
      }
    });
    return options;
  }

  useEffect(() => {
    setSearchQuery(selectedValue?.ensemblName ?? '');
  }, [selectedValue]);

  function genomeChangeHandler(option: GenomeOption): void {
    setSearchQuery(option.ensemblName);
    onSelectGenome(option);
  }

  function matchGenome(escapedTerms: Array<string>, data: GenomeOption): boolean {
    // Search for substrings across all genome names. For example, 'd m' matches 'Drosophila melanogaster'
    // Return true if each term matches something in the genome
    const corpus = [
      data.ucscName,
      data.organismName,
      data.ensemblName,
      data.displayName,
      data.speciesName,
      ...(data?.organism?.keywords ?? []),
      ...(data?.aliases ?? []),
    ].filter((str) => str != null && str.length > 0);

    return escapedTerms.every((term: string) => {
      const regex = new RegExp(term, 'i');
      return corpus.some((name: string) => regex.test(name));
    });
  }

  function hasNoResults(): boolean {
    return Object.values(menuOptions).every((options) => options.length === 0);
  }

  useEffect(() => {
    if (searchQueryDebounced.length === 0) {
      setMenuOptions(allMenuOptions);
    } else {
      const escaped = escapeRegExp(searchQueryDebounced);
      const terms = _.compact(escaped.split(/\s+/));
      const results: {
        [key in GenomeSource]: Array<GenomeOption>;
      } = {[GenomeSource.ENSEMBL]: [], [GenomeSource.PUBLIC]: [], [GenomeSource.PRIVATE]: []};
      Object.entries(allMenuOptions).forEach(([section, options]: [GenomeSource, Array<GenomeOption>]) => {
        results[section] = options.filter((data) => matchGenome(terms, data));
      });
      setMenuOptions(results);
    }
  }, [searchQueryDebounced, allMenuOptions]);

  return (
    <div>
      <Dropdown
        alwaysDropDown={true}
        isOpen={isDropdownOpen}
        menu={
          <Menu value={selectedValue} height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL} scrollable={true}>
            {hasNoResults() ? (
              <Option isDisabled={true}>
                <div>No species found</div>
              </Option>
            ) : (
              Object.entries(menuOptions).map(([header, options]: [GenomeSource, Array<GenomeOption>]) =>
                options.length > 0 ? (
                  <Section key={header}>
                    {GENOME_SECTION_HEADER[header] && (
                      <SectionHeader>{GENOME_SECTION_HEADER[header]}</SectionHeader>
                    )}
                    {options.slice(0, MAX_ENSEMBL_OPTIONS).map((option: GenomeOption) => (
                      <Option
                        key={option.speciesName ?? option.ensemblName}
                        value={option}
                        onSelect={genomeChangeHandler}
                        dataTestAttrs={{component: 'GenomeSearchSelector', element: 'dropdown'}}
                      >
                        <>
                          <div>{option.displayName}</div>
                          {header === GenomeSource.ENSEMBL && (
                            <div className={css(styles.optionSubtext)}>
                              {[option.ensemblName, ...(option.aliases ?? [])].join(', ')}
                            </div>
                          )}
                        </>
                      </Option>
                    ))}
                  </Section>
                ) : null
              )
            )}
            {menuOptions[GenomeSource.ENSEMBL].length > MAX_ENSEMBL_OPTIONS && (
              <Option isDisabled={true}>
                <div>Enter a search query to load more results</div>
              </Option>
            )}
          </Menu>
        }
      >
        <TextInput
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setSearchQuery(e.target.value);
          }}
          onFocus={() => {
            setIsDropdownOpen(true);
          }}
          onBlur={() => {
            setSearchQuery(selectedValue?.ensemblName ?? '');
            setIsDropdownOpen(false);
          }}
          placeholder="Enter species name or alias"
          dataTestAttrs={{component: 'GenomeSearchSelector', element: 'textInput'}}
          value={searchQuery}
          disabled={disabled}
        />
      </Dropdown>
    </div>
  );
}

const styles = StyleSheet.create({
  optionSubtext: {
    ...FONT_PROXIMA_12,
    color: GRAY_DARK,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});

export const GenomeSearchSelectorDAO = {
  fetch: async (): Promise<Array<GenomeOption>> => {
    const resp = await fetch(ENSEMBL_SPECIES_LINK);
    return await resp.json();
  },
};

export default function GenomeSearchSelector(props: GenomeSearchSelectorProps): JSX.Element | null {
  const state = useAsyncStateOnMount<Array<GenomeOption>>(GenomeSearchSelectorDAO.fetch);
  switch (state.status) {
    case AsyncStatus.NOT_FETCHED:
    case AsyncStatus.LOADING:
      return <CenteredHorizontalThrobber />;
    case AsyncStatus.SUCCESS:
      return <PresentationalGenomeSearchSelector {...props} ensemblSpecies={state.data} />;
    case AsyncStatus.ERROR:
      return null;
    default:
      assert(false, 'Got unexpected status', assertNever(state));
  }
}
